import React, { useEffect, useRef, useState } from 'react';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider, useData } from './contexts/DataContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ExerciseDemoProvider } from './contexts/ExerciseDemoContext';
import { AuthForm } from './components/AuthForm';
import { ResetPasswordForm } from './components/ResetPasswordForm';
import { OAuthConsent, isOAuthConsentPath } from './components/OAuthConsent';
import { Layout } from './components/Layout';
import { SettingsModal } from './components/SettingsModal';
import { RoutinesTab } from './components/RoutinesTab';
import { WorkoutTab } from './components/WorkoutTab';
import { ProgressTab } from './components/ProgressTab';
import type { TabType } from './types';

interface GymTrackerAppProps {
  activeTab: TabType;
  setActiveTab: (tab: TabType) => void;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
}

function GymTrackerApp({ activeTab, setActiveTab, showSettings, setShowSettings }: GymTrackerAppProps) {
  const { theme } = useTheme();
  const { user, authLoading, isPasswordRecovery } = useAuth();
  const { dataLoading, isDemoMode, isOffline, pendingCount, getActivePlan, selectedRoutine } = useData();
  const activePlan = getActivePlan();
  const didPrioritizeTodayRef = useRef(false);

  useEffect(() => {
    if (selectedRoutine) {
      didPrioritizeTodayRef.current = true;
    }
  }, [selectedRoutine]);

  useEffect(() => {
    if (!user) {
      didPrioritizeTodayRef.current = false;
      return;
    }

    if (dataLoading || didPrioritizeTodayRef.current || selectedRoutine || !activePlan) {
      return;
    }

    setActiveTab('workout');
    didPrioritizeTodayRef.current = true;
  }, [user, dataLoading, selectedRoutine, activePlan, setActiveTab]);

  if (authLoading) {
    return (
      <div className={`min-h-screen ${theme.bg} flex items-center justify-center`}>
        <div className='text-center'>
          <div className='w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4' />
          <p className={theme.textSecondary}>Cargando...</p>
        </div>
      </div>
    );
  }

  if (isPasswordRecovery) {
    // user puede ser null mientras Supabase establece la sesión de recovery.
    // authLoading ya es false aquí (pasó el gate de arriba), así que si user
    // sigue siendo null significa que el token es inválido o expiró — mostramos
    // AuthForm en vez de un spinner infinito.
    if (!user) return <AuthForm />;
    return <ResetPasswordForm />;
  }

  // Consentimiento OAuth para clientes MCP. El login no reescribe la URL, así
  // que authorization_id sigue en la query después de iniciar sesión.
  if (isOAuthConsentPath(window.location.pathname)) {
    return user ? <OAuthConsent /> : <AuthForm />;
  }

  if (!user) {
    return <AuthForm />;
  }

  return (
    <ExerciseDemoProvider>
      <Layout
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        showSettings={showSettings}
        setShowSettings={setShowSettings}
        dataLoading={dataLoading}
        isDemoMode={isDemoMode}
        hasActivePlan={!!activePlan}
        isOffline={isOffline}
        pendingCount={pendingCount}
      >
        {activeTab === 'routines' && <RoutinesTab />}
        {activeTab === 'workout' && <WorkoutTab setActiveTab={setActiveTab} />}
        {activeTab === 'progress' && <ProgressTab setActiveTab={setActiveTab} />}
      </Layout>

      {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    </ExerciseDemoProvider>
  );
}

function GymTrackerWithProviders() {
  const [activeTab, setActiveTab] = useState<TabType>('routines');
  const [showSettings, setShowSettings] = useState(false);

  return (
    <ThemeProvider>
      <AuthProvider>
        <NotificationProvider>
          <DataProvider
            setActiveTab={setActiveTab}
            setShowSettings={setShowSettings}
            setSelectedWorkoutDetail={() => {}}
          >
            <GymTrackerApp
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              showSettings={showSettings}
              setShowSettings={setShowSettings}
            />
          </DataProvider>
        </NotificationProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default function App() {
  return <GymTrackerWithProviders />;
}
